/**
 * QUICK REACTION ASSETS & VECTOR FALLBACKS (HU-06)
 * Biblioteca de recursos gráficos, precarga proactiva en memoria RAM (new Image()),
 * fallbacks SVG vectoriales inmutables y resolución segura de URLs multimedia para
 * la Pantalla Gigante de Estadio (/screen y /screen-v2) en la dinámica Reacción Rápida (1 vs 1).
 * 
 * Arquitectura Zero-Canvas: Renderizado 100% acelerado por GPU (translate3d, will-change: transform).
 * Transiciones instantáneas a 0ms sin parpadeos ni pop-in visual.
 * 
 * Seguridad:
 * - Sanitización estricta de URLs con lista blanca de esquemas (http, https, blob, data:image/...).
 * - Prevención de Path Traversal, CRLF injection, control characters y HTML/CSS injection.
 * - Ciclo de vida de precarga con timeout defensivo y desvinculación explícita de handlers (Zero Memory Leaks).
 * - SVGs vectoriales inmutables y codificados de forma segura para evitar syntax breakage en CSS url().
 * 
 * @module components/Screen/Sections/QuickReaction/QuickReactionAssets
 */

/**
 * Codifica de forma segura un string SVG XML en un Data URI UTF-8 inmutable,
 * garantizando compatibilidad 100% tanto en <img src="..."> como en CSS background-image: url("...").
 * 
 * @param {string} svgXml - Contenido XML del SVG.
 * @returns {string} Data URI codificado de forma segura.
 */
const encodeSvgDataUri = (svgXml) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgXml.trim())}`;

/**
 * Resuelve una ruta o URL de recurso multimedia hacia una URL absoluta y sanitizada.
 * Bloquea esquemas ejecutables maliciosos (javascript:, vbscript:, data: no-imagen),
 * previene Path Traversal (/../, %2e%2e), inyección CRLF y caracteres que rompen HTML/CSS,
 * y antepone el origen del servidor API cuando la ruta proporcionada es relativa.
 * 
 * @function ResolveQuickReactionMediaUrl
 * @param {string|null|undefined} mediaPath - Ruta relativa o URL absoluta a resolver.
 * @param {string} [fallback=''] - Recurso de respaldo inmutable en caso de ruta nula o inválida.
 * @returns {string} URL absoluta lista para ser renderizada por el navegador.
 */
export const ResolveQuickReactionMediaUrl = (mediaPath, fallback = '') => {
    if (!mediaPath || typeof mediaPath !== 'string') return fallback || '';

    // 1. Eliminación de caracteres de control (CRLF, Null bytes, tabs) y espacios en extremos
    const cleanPath = mediaPath.replace(/[\x00-\x1F\x7F]/g, '').trim();
    if (!cleanPath) return fallback || '';

    // 2. Bloqueo estricto de secuencias de Path Traversal (../, ..\, %2e%2e)
    if (/(?:^|\/|\\)\.\.(?:\/|\\|$)/.test(cleanPath) || /%2e%2e/i.test(cleanPath)) {
        console.warn('[QuickReactionAssets] URL bloqueada por intento de Path Traversal:', cleanPath.substring(0, 40));
        return fallback || '';
    }

    // 3. Bloqueo de caracteres prohibidos para inyección HTML / CSS url() / scripts
    if (/["'`<>]/.test(cleanPath)) {
        console.warn('[QuickReactionAssets] URL contiene caracteres prohibidos para AppSec:', cleanPath);
        return fallback || '';
    }

    // 4. Validación estricta de esquemas mediante lista blanca (Scheme Whitelist)
    const schemeMatch = cleanPath.match(/^([a-zA-Z][a-zA-Z0-9+.-]*):/);
    if (schemeMatch) {
        const scheme = schemeMatch[1].toLowerCase();

        // Esquemas permitidos: http, https, blob
        if (scheme === 'http' || scheme === 'https' || scheme === 'blob') {
            return cleanPath;
        }

        // Esquema data: solo permitido si es MIME type de imagen seguro
        if (scheme === 'data') {
            if (/^data:image\/(?:png|jpe?g|webp|gif|svg\+xml)(?:;[a-zA-Z0-9=+ -]+)*,/i.test(cleanPath)) {
                return cleanPath;
            }
            console.warn('[QuickReactionAssets] data: URI bloqueado por MIME type no permitido:', cleanPath.substring(0, 50));
            return fallback || '';
        }

        // Cualquier otro esquema (javascript, vbscript, file, etc.) es bloqueado
        console.warn('[QuickReactionAssets] URL bloqueada por esquema no permitido:', scheme);
        return fallback || '';
    }

    // 5. Resolución de ruta relativa contra el Backend Origin sanitizado
    const apiBase = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    const backendOrigin = apiBase.replace(/\/api\/?$/i, '');
    const normalizedPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
    return `${backendOrigin}${normalizedPath}`;
};

/**
 * Precarga de forma proactiva y asíncrona en la memoria RAM del navegador (usando `new Image()`)
 * todas las imágenes del tema visual activo de Reacción Rápida:
 * 1. Fondo de Pantalla Gigante (1920x1080)
 * 2. Fondo de Dispositivo Móvil (1080x1920)
 * 3. Logotipo oficial / patrocinador
 * 4. Los 8 Sprites de combate (4 fases para J1 y 4 fases para J2):
 *    - J1: 1. Espaldas, 2. Frente, 3. Disparo, 4. Victoria
 *    - J2: 1. Espaldas, 2. Frente, 3. Disparo, 4. Victoria
 * 
 * Garantiza transiciones a 60 FPS sin parpadeos (0ms visual pop-in), deduplicación de URLs,
 * protección contra fugas de memoria (limpieza de handlers onload/onerror) y timeout de seguridad.
 * 
 * @function PreloadQuickReactionThemeAssets
 * @param {Object|null} theme - Objeto de configuración del tema activo.
 * @returns {Promise<Array<{url: string, success: boolean}>>} Promesa resuelta con el resultado individual de carga.
 */
export const PreloadQuickReactionThemeAssets = (theme) => {
    if (!theme || typeof theme !== 'object') {
        return Promise.resolve([]);
    }

    const rawUrls = [
        theme.screenBackgroundImageUrl || theme.ScreenBackgroundImageUrl || theme.screenBgUrl,
        theme.mobileBackgroundImageUrl || theme.MobileBackgroundImageUrl || theme.mobileBgUrl,
        theme.logoImageUrl || theme.LogoImageUrl || theme.brandLogoUrl,
        // Sprites Jugador 1
        theme.j1SpriteBackUrl || theme.J1SpriteBackUrl,
        theme.j1SpriteFrontUrl || theme.J1SpriteFrontUrl,
        theme.j1SpriteShootUrl || theme.J1SpriteShootUrl,
        theme.j1SpriteVictoryUrl || theme.J1SpriteVictoryUrl,
        // Sprites Jugador 2
        theme.j2SpriteBackUrl || theme.J2SpriteBackUrl,
        theme.j2SpriteFrontUrl || theme.J2SpriteFrontUrl,
        theme.j2SpriteShootUrl || theme.J2SpriteShootUrl,
        theme.j2SpriteVictoryUrl || theme.J2SpriteVictoryUrl
    ];

    // Deduplicar y resolver URLs válidas omitiendo Data URIs ya en memoria
    const validUrls = Array.from(new Set(
        rawUrls
            .filter((url) => typeof url === 'string' && url.trim().length > 0)
            .map((url) => ResolveQuickReactionMediaUrl(url))
            .filter((url) => url && !url.startsWith('data:'))
    ));

    const loadPromises = validUrls.map((url) => {
        return new Promise((resolve) => {
            let settled = false;
            const img = new Image();

            const onSettled = (success) => {
                if (settled) return;
                settled = true;
                if (safetyTimeoutId) clearTimeout(safetyTimeoutId);
                img.onload = null;
                img.onerror = null;
                img.onabort = null;
                resolve({ url, success });
            };

            // Timeout de seguridad de 8 segundos para evitar promesas colgadas en redes lentas
            const safetyTimeoutId = setTimeout(() => {
                onSettled(false);
            }, 8000);

            img.onload = () => onSettled(true);
            img.onerror = () => onSettled(false);
            img.onabort = () => onSettled(false);
            img.src = url;
        });
    });

    return Promise.all(loadPromises);
};

// =========================================================================
// FALLBACKS SVG VECTORIALES INMUTABLES (ZERO-CANVAS 60 FPS - CODIFICADOS)
// =========================================================================

/**
 * Sprite 1 Jugador 1: De espaldas (Bando Azul / Neón Cian).
 * @constant {string}
 */
export const DEFAULT_J1_BACK_SVG = encodeSvgDataUri(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 500" width="400" height="500">
  <defs>
    <radialGradient id="j1BackAura" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="#00f0ff" stop-opacity="0.85" />
      <stop offset="60%" stop-color="#0284c7" stop-opacity="0.3" />
      <stop offset="100%" stop-color="#0c192c" stop-opacity="0" />
    </radialGradient>
    <linearGradient id="j1SuitGrad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#0284c7" />
      <stop offset="60%" stop-color="#091e3a" />
      <stop offset="100%" stop-color="#00f0ff" />
    </linearGradient>
    <filter id="j1Glow" x="-20%" y="-20%" width="140%" height="140%">
      <feGaussianBlur stdDeviation="6" result="blur" />
      <feComposite in="SourceGraphic" in2="blur" operator="over" />
    </filter>
  </defs>
  <circle cx="200" cy="250" r="180" fill="url(#j1BackAura)" />
  <ellipse cx="200" cy="110" rx="65" ry="30" fill="#081b2e" stroke="#00f0ff" stroke-width="4" filter="url(#j1Glow)" />
  <path d="M160 110 Q200 65 240 110 Z" fill="#0369a1" stroke="#38bdf8" stroke-width="3" />
  <path d="M110 160 Q200 130 290 160 L320 380 Q200 420 80 380 Z" fill="url(#j1SuitGrad)" stroke="#00f0ff" stroke-width="4" />
  <rect x="130" y="300" width="140" height="24" rx="4" fill="#081b2e" stroke="#38bdf8" stroke-width="3" />
  <path d="M115 310 L95 380 L125 380 L135 310 Z" fill="#0284c7" stroke="#00f0ff" stroke-width="3" />
  <path d="M285 310 L305 380 L275 380 L265 310 Z" fill="#0284c7" stroke="#00f0ff" stroke-width="3" />
  <line x1="200" y1="160" x2="200" y2="300" stroke="#00f0ff" stroke-width="5" stroke-linecap="round" filter="url(#j1Glow)" />
  <line x1="165" y1="200" x2="165" y2="280" stroke="#38bdf8" stroke-width="3" />
  <line x1="235" y1="200" x2="235" y2="280" stroke="#38bdf8" stroke-width="3" />
  <path d="M130 380 L120 480 L170 480 L185 380 Z" fill="#091e3a" stroke="#00f0ff" stroke-width="3" />
  <path d="M270 380 L280 480 L230 480 L215 380 Z" fill="#091e3a" stroke="#00f0ff" stroke-width="3" />
</svg>`);

/**
 * Sprite 2 Jugador 1: De frente preparado (Bando Azul / Neón Cian).
 * @constant {string}
 */
export const DEFAULT_J1_FRONT_SVG = encodeSvgDataUri(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 500" width="400" height="500">
  <defs>
    <radialGradient id="j1FrontAura" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="#00f0ff" stop-opacity="0.9" />
      <stop offset="60%" stop-color="#0284c7" stop-opacity="0.4" />
      <stop offset="100%" stop-color="#0c192c" stop-opacity="0" />
    </radialGradient>
    <linearGradient id="j1SuitFrontGrad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#0284c7" />
      <stop offset="50%" stop-color="#081b2e" />
      <stop offset="100%" stop-color="#00f0ff" />
    </linearGradient>
    <filter id="j1FrontGlow" x="-20%" y="-20%" width="140%" height="140%">
      <feGaussianBlur stdDeviation="6" result="blur" />
      <feComposite in="SourceGraphic" in2="blur" operator="over" />
    </filter>
  </defs>
  <circle cx="200" cy="250" r="180" fill="url(#j1FrontAura)" />
  <ellipse cx="200" cy="100" rx="60" ry="25" fill="#081b2e" stroke="#00f0ff" stroke-width="4" filter="url(#j1FrontGlow)" />
  <path d="M150 100 Q200 50 250 100 Z" fill="#0284c7" stroke="#38bdf8" stroke-width="3" />
  <polygon points="160,110 240,110 230,125 170,125" fill="#00f0ff" filter="url(#j1FrontGlow)" />
  <path d="M120 140 L280 140 L260 320 L140 320 Z" fill="url(#j1SuitFrontGrad)" stroke="#00f0ff" stroke-width="4" />
  <polygon points="170,150 230,150 220,240 180,240" fill="#0369a1" stroke="#38bdf8" stroke-width="2.5" />
  <path d="M120 150 L80 240 L110 310" fill="none" stroke="#0284c7" stroke-width="22" stroke-linecap="round" stroke-linejoin="round" />
  <path d="M280 150 L320 240 L290 310" fill="none" stroke="#0284c7" stroke-width="22" stroke-linecap="round" stroke-linejoin="round" />
  <circle cx="110" cy="310" r="16" fill="#00f0ff" filter="url(#j1FrontGlow)" />
  <circle cx="290" cy="310" r="16" fill="#00f0ff" filter="url(#j1FrontGlow)" />
  <rect x="135" y="315" width="130" height="20" rx="3" fill="#081b2e" stroke="#00f0ff" stroke-width="3" />
  <path d="M140 335 L120 480 L165 480 L180 335 Z" fill="#091e3a" stroke="#00f0ff" stroke-width="3" />
  <path d="M260 335 L280 480 L235 480 L220 335 Z" fill="#091e3a" stroke="#00f0ff" stroke-width="3" />
</svg>`);

/**
 * Sprite 3 Jugador 1: Disparando (Bando Azul / Neón Cian).
 * @constant {string}
 */
export const DEFAULT_J1_SHOOT_SVG = encodeSvgDataUri(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 450 500" width="450" height="500">
  <defs>
    <radialGradient id="j1ShootAura" cx="60%" cy="40%" r="50%">
      <stop offset="0%" stop-color="#00f0ff" stop-opacity="1" />
      <stop offset="50%" stop-color="#0284c7" stop-opacity="0.5" />
      <stop offset="100%" stop-color="#0c192c" stop-opacity="0" />
    </radialGradient>
    <filter id="j1ShootGlow" x="-30%" y="-30%" width="160%" height="160%">
      <feGaussianBlur stdDeviation="8" result="blur" />
      <feComposite in="SourceGraphic" in2="blur" operator="over" />
    </filter>
  </defs>
  <circle cx="260" cy="200" r="180" fill="url(#j1ShootAura)" />
  <ellipse cx="160" cy="90" rx="55" ry="24" fill="#081b2e" stroke="#00f0ff" stroke-width="4" filter="url(#j1ShootGlow)" />
  <path d="M115 90 Q160 45 205 90 Z" fill="#0284c7" stroke="#38bdf8" stroke-width="3" />
  <polygon points="125,100 195,100 185,115 135,115" fill="#00f0ff" filter="url(#j1ShootGlow)" />
  <path d="M100 130 L220 130 L200 310 L90 310 Z" fill="#091e3a" stroke="#00f0ff" stroke-width="4" />
  <path d="M190 140 L310 140 L360 140" stroke="#0284c7" stroke-width="26" stroke-linecap="round" />
  <rect x="340" y="125" width="45" height="22" rx="3" fill="#081b2e" stroke="#00f0ff" stroke-width="3" />
  <polygon points="385,136 430,110 410,136 445,136 410,146 435,165 385,142" fill="#00f0ff" filter="url(#j1ShootGlow)" />
  <line x1="390" y1="136" x2="450" y2="136" stroke="#ffffff" stroke-width="5" stroke-linecap="round" />
  <path d="M100 325 L60 470 L110 470 L140 325 Z" fill="#0369a1" stroke="#00f0ff" stroke-width="3" />
  <path d="M190 325 L240 470 L195 470 L160 325 Z" fill="#0369a1" stroke="#00f0ff" stroke-width="3" />
</svg>`);

/**
 * Sprite 4 Jugador 1: Victoria con Halo y Trofeo Dorado (Bando Azul / Neón Cian).
 * @constant {string}
 */
export const DEFAULT_J1_VICTORY_SVG = encodeSvgDataUri(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 450 500" width="450" height="500">
  <defs>
    <radialGradient id="j1VicAura" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="#ffea00" stop-opacity="0.9" />
      <stop offset="45%" stop-color="#00f0ff" stop-opacity="0.6" />
      <stop offset="100%" stop-color="#0c192c" stop-opacity="0" />
    </radialGradient>
    <filter id="j1VicGlow" x="-20%" y="-20%" width="140%" height="140%">
      <feGaussianBlur stdDeviation="8" result="blur" />
      <feComposite in="SourceGraphic" in2="blur" operator="over" />
    </filter>
  </defs>
  <circle cx="225" cy="240" r="210" fill="url(#j1VicAura)" />
  <g stroke="#ffea00" stroke-width="3" opacity="0.85">
    <line x1="225" y1="30" x2="225" y2="75" />
    <line x1="360" y1="80" x2="330" y2="120" />
    <line x1="420" y1="220" x2="375" y2="220" />
    <line x1="90" y1="80" x2="120" y2="120" />
    <line x1="30" y1="220" x2="75" y2="220" />
  </g>
  <ellipse cx="225" cy="120" rx="55" ry="24" fill="#081b2e" stroke="#ffea00" stroke-width="4" filter="url(#j1VicGlow)" />
  <path d="M180 120 Q225 75 270 120 Z" fill="#0284c7" stroke="#ffea00" stroke-width="3" />
  <polygon points="190,130 260,130 250,145 200,145" fill="#00f0ff" filter="url(#j1VicGlow)" />
  <path d="M145 160 L305 160 L285 340 L165 340 Z" fill="#091e3a" stroke="#00f0ff" stroke-width="4" />
  <path d="M145 170 L80 110 L95 60" fill="none" stroke="#0284c7" stroke-width="22" stroke-linecap="round" />
  <path d="M305 170 L370 110 L355 60" fill="none" stroke="#0284c7" stroke-width="22" stroke-linecap="round" />
  <rect x="85" y="40" width="20" height="35" rx="3" fill="#081b2e" stroke="#00f0ff" stroke-width="3" />
  <rect x="345" y="40" width="20" height="35" rx="3" fill="#081b2e" stroke="#00f0ff" stroke-width="3" />
  <circle cx="95" cy="30" r="10" fill="#ffea00" opacity="0.8" filter="url(#j1VicGlow)" />
  <circle cx="355" cy="30" r="10" fill="#ffea00" opacity="0.8" filter="url(#j1VicGlow)" />
  <rect x="155" y="325" width="140" height="28" rx="4" fill="#b45309" stroke="#ffea00" stroke-width="4" />
  <polygon points="225,325 250,340 225,355 200,340" fill="#ffea00" />
  <path d="M165 355 L145 480 L195 480 L210 355 Z" fill="#0369a1" stroke="#00f0ff" stroke-width="3" />
  <path d="M285 355 L305 480 L255 480 L240 355 Z" fill="#0369a1" stroke="#00f0ff" stroke-width="3" />
</svg>`);

/**
 * Sprite 1 Jugador 2: De espaldas (Bando Rojo / Neón Carmesí).
 * @constant {string}
 */
export const DEFAULT_J2_BACK_SVG = encodeSvgDataUri(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 500" width="400" height="500">
  <defs>
    <radialGradient id="j2BackAura" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="#ff0055" stop-opacity="0.85" />
      <stop offset="60%" stop-color="#990033" stop-opacity="0.3" />
      <stop offset="100%" stop-color="#20040f" stop-opacity="0" />
    </radialGradient>
    <linearGradient id="j2SuitGrad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#e11d48" />
      <stop offset="60%" stop-color="#380415" />
      <stop offset="100%" stop-color="#ff0055" />
    </linearGradient>
    <filter id="j2Glow" x="-20%" y="-20%" width="140%" height="140%">
      <feGaussianBlur stdDeviation="6" result="blur" />
      <feComposite in="SourceGraphic" in2="blur" operator="over" />
    </filter>
  </defs>
  <circle cx="200" cy="250" r="180" fill="url(#j2BackAura)" />
  <ellipse cx="200" cy="110" rx="65" ry="30" fill="#2b0716" stroke="#ff0055" stroke-width="4" filter="url(#j2Glow)" />
  <path d="M160 110 Q200 65 240 110 Z" fill="#be123c" stroke="#fb7185" stroke-width="3" />
  <path d="M110 160 Q200 130 290 160 L320 380 Q200 420 80 380 Z" fill="url(#j2SuitGrad)" stroke="#ff0055" stroke-width="4" />
  <rect x="130" y="300" width="140" height="24" rx="4" fill="#2b0716" stroke="#fb7185" stroke-width="3" />
  <path d="M115 310 L95 380 L125 380 L135 310 Z" fill="#be123c" stroke="#ff0055" stroke-width="3" />
  <path d="M285 310 L305 380 L275 380 L265 310 Z" fill="#be123c" stroke="#ff0055" stroke-width="3" />
  <line x1="200" y1="160" x2="200" y2="300" stroke="#ff0055" stroke-width="5" stroke-linecap="round" filter="url(#j2Glow)" />
  <line x1="165" y1="200" x2="165" y2="280" stroke="#fb7185" stroke-width="3" />
  <line x1="235" y1="200" x2="235" y2="280" stroke="#fb7185" stroke-width="3" />
  <path d="M130 380 L120 480 L170 480 L185 380 Z" fill="#380415" stroke="#ff0055" stroke-width="3" />
  <path d="M270 380 L280 480 L230 480 L215 380 Z" fill="#380415" stroke="#ff0055" stroke-width="3" />
</svg>`);

/**
 * Sprite 2 Jugador 2: De frente preparado (Bando Rojo / Neón Carmesí).
 * @constant {string}
 */
export const DEFAULT_J2_FRONT_SVG = encodeSvgDataUri(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 500" width="400" height="500">
  <defs>
    <radialGradient id="j2FrontAura" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="#ff0055" stop-opacity="0.9" />
      <stop offset="60%" stop-color="#990033" stop-opacity="0.4" />
      <stop offset="100%" stop-color="#20040f" stop-opacity="0" />
    </radialGradient>
    <linearGradient id="j2SuitFrontGrad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#e11d48" />
      <stop offset="50%" stop-color="#2b0716" />
      <stop offset="100%" stop-color="#ff0055" />
    </linearGradient>
    <filter id="j2FrontGlow" x="-20%" y="-20%" width="140%" height="140%">
      <feGaussianBlur stdDeviation="6" result="blur" />
      <feComposite in="SourceGraphic" in2="blur" operator="over" />
    </filter>
  </defs>
  <circle cx="200" cy="250" r="180" fill="url(#j2FrontAura)" />
  <ellipse cx="200" cy="100" rx="60" ry="25" fill="#2b0716" stroke="#ff0055" stroke-width="4" filter="url(#j2FrontGlow)" />
  <path d="M150 100 Q200 50 250 100 Z" fill="#be123c" stroke="#fb7185" stroke-width="3" />
  <polygon points="160,110 240,110 230,125 170,125" fill="#ff0055" filter="url(#j2FrontGlow)" />
  <path d="M120 140 L280 140 L260 320 L140 320 Z" fill="url(#j2SuitFrontGrad)" stroke="#ff0055" stroke-width="4" />
  <polygon points="170,150 230,150 220,240 180,240" fill="#881337" stroke="#fb7185" stroke-width="2.5" />
  <path d="M120 150 L80 240 L110 310" fill="none" stroke="#be123c" stroke-width="22" stroke-linecap="round" stroke-linejoin="round" />
  <path d="M280 150 L320 240 L290 310" fill="none" stroke="#be123c" stroke-width="22" stroke-linecap="round" stroke-linejoin="round" />
  <circle cx="110" cy="310" r="16" fill="#ff0055" filter="url(#j2FrontGlow)" />
  <circle cx="290" cy="310" r="16" fill="#ff0055" filter="url(#j2FrontGlow)" />
  <rect x="135" y="315" width="130" height="20" rx="3" fill="#2b0716" stroke="#ff0055" stroke-width="3" />
  <path d="M140 335 L120 480 L165 480 L180 335 Z" fill="#380415" stroke="#ff0055" stroke-width="3" />
  <path d="M260 335 L280 480 L235 480 L220 335 Z" fill="#380415" stroke="#ff0055" stroke-width="3" />
</svg>`);

/**
 * Sprite 3 Jugador 2: Disparando (Bando Rojo / Neón Carmesí).
 * @constant {string}
 */
export const DEFAULT_J2_SHOOT_SVG = encodeSvgDataUri(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 450 500" width="450" height="500">
  <defs>
    <radialGradient id="j2ShootAura" cx="40%" cy="40%" r="50%">
      <stop offset="0%" stop-color="#ff0055" stop-opacity="1" />
      <stop offset="50%" stop-color="#990033" stop-opacity="0.5" />
      <stop offset="100%" stop-color="#20040f" stop-opacity="0" />
    </radialGradient>
    <filter id="j2ShootGlow" x="-30%" y="-30%" width="160%" height="160%">
      <feGaussianBlur stdDeviation="8" result="blur" />
      <feComposite in="SourceGraphic" in2="blur" operator="over" />
    </filter>
  </defs>
  <circle cx="190" cy="200" r="180" fill="url(#j2ShootAura)" />
  <ellipse cx="290" cy="90" rx="55" ry="24" fill="#2b0716" stroke="#ff0055" stroke-width="4" filter="url(#j2ShootGlow)" />
  <path d="M245 90 Q290 45 335 90 Z" fill="#be123c" stroke="#fb7185" stroke-width="3" />
  <polygon points="255,100 325,100 315,115 265,115" fill="#ff0055" filter="url(#j2ShootGlow)" />
  <path d="M230 130 L350 130 L360 310 L250 310 Z" fill="#380415" stroke="#ff0055" stroke-width="4" />
  <path d="M260 140 L140 140 L90 140" stroke="#be123c" stroke-width="26" stroke-linecap="round" />
  <rect x="65" y="125" width="45" height="22" rx="3" fill="#2b0716" stroke="#ff0055" stroke-width="3" />
  <polygon points="65,136 20,110 40,136 5,136 40,146 15,165 65,142" fill="#ff0055" filter="url(#j2ShootGlow)" />
  <line x1="60" y1="136" x2="0" y2="136" stroke="#ffffff" stroke-width="5" stroke-linecap="round" />
  <path d="M350 325 L390 470 L340 470 L310 325 Z" fill="#881337" stroke="#ff0055" stroke-width="3" />
  <path d="M260 325 L210 470 L255 470 L290 325 Z" fill="#881337" stroke="#ff0055" stroke-width="3" />
</svg>`);

/**
 * Sprite 4 Jugador 2: Victoria con Halo y Trofeo Dorado (Bando Rojo / Neón Carmesí).
 * @constant {string}
 */
export const DEFAULT_J2_VICTORY_SVG = encodeSvgDataUri(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 450 500" width="450" height="500">
  <defs>
    <radialGradient id="j2VicAura" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="#ffea00" stop-opacity="0.9" />
      <stop offset="45%" stop-color="#ff0055" stop-opacity="0.6" />
      <stop offset="100%" stop-color="#20040f" stop-opacity="0" />
    </radialGradient>
    <filter id="j2VicGlow" x="-20%" y="-20%" width="140%" height="140%">
      <feGaussianBlur stdDeviation="8" result="blur" />
      <feComposite in="SourceGraphic" in2="blur" operator="over" />
    </filter>
  </defs>
  <circle cx="225" cy="240" r="210" fill="url(#j2VicAura)" />
  <g stroke="#ffea00" stroke-width="3" opacity="0.85">
    <line x1="225" y1="30" x2="225" y2="75" />
    <line x1="360" y1="80" x2="330" y2="120" />
    <line x1="420" y1="220" x2="375" y2="220" />
    <line x1="90" y1="80" x2="120" y2="120" />
    <line x1="30" y1="220" x2="75" y2="220" />
  </g>
  <ellipse cx="225" cy="120" rx="55" ry="24" fill="#2b0716" stroke="#ffea00" stroke-width="4" filter="url(#j2VicGlow)" />
  <path d="M180 120 Q225 75 270 120 Z" fill="#be123c" stroke="#ffea00" stroke-width="3" />
  <polygon points="190,130 260,130 250,145 200,145" fill="#ff0055" filter="url(#j2VicGlow)" />
  <path d="M145 160 L305 160 L285 340 L165 340 Z" fill="#380415" stroke="#ff0055" stroke-width="4" />
  <path d="M145 170 L80 110 L95 60" fill="none" stroke="#be123c" stroke-width="22" stroke-linecap="round" />
  <path d="M305 170 L370 110 L355 60" fill="none" stroke="#be123c" stroke-width="22" stroke-linecap="round" />
  <rect x="85" y="40" width="20" height="35" rx="3" fill="#2b0716" stroke="#ff0055" stroke-width="3" />
  <rect x="345" y="40" width="20" height="35" rx="3" fill="#2b0716" stroke="#ff0055" stroke-width="3" />
  <circle cx="95" cy="30" r="10" fill="#ffea00" opacity="0.8" filter="url(#j2VicGlow)" />
  <circle cx="355" cy="30" r="10" fill="#ffea00" opacity="0.8" filter="url(#j2VicGlow)" />
  <rect x="155" y="325" width="140" height="28" rx="4" fill="#b45309" stroke="#ffea00" stroke-width="4" />
  <polygon points="225,325 250,340 225,355 200,340" fill="#ffea00" />
  <path d="M165 355 L145 480 L195 480 L210 355 Z" fill="#881337" stroke="#ff0055" stroke-width="3" />
  <path d="M285 355 L305 480 L255 480 L240 355 Z" fill="#881337" stroke="#ff0055" stroke-width="3" />
</svg>`);

/**
 * Fondo de Pantalla Gigante por Defecto (1920x1080 Full HD).
 * Arena Cyberpunk Neón con reflectores de duelo 1 vs 1.
 * @constant {string}
 */
export const DEFAULT_SCREEN_BG_SVG = encodeSvgDataUri(`<svg xmlns="http://www.w3.org/2000/svg" width="1920" height="1080" viewBox="0 0 1920 1080">
  <defs>
    <radialGradient id="qrArenaGlow" cx="50%" cy="50%" r="60%">
      <stop offset="0%" stop-color="#1e0b36" stop-opacity="0.95" />
      <stop offset="50%" stop-color="#0d041a" stop-opacity="0.98" />
      <stop offset="100%" stop-color="#030008" stop-opacity="1" />
    </radialGradient>
    <linearGradient id="qrLaserBlue" x1="0%" y1="0%" x2="50%" y2="100%">
      <stop offset="0%" stop-color="#00f0ff" stop-opacity="0.4" />
      <stop offset="100%" stop-color="#00f0ff" stop-opacity="0" />
    </linearGradient>
    <linearGradient id="qrLaserRed" x1="100%" y1="0%" x2="50%" y2="100%">
      <stop offset="0%" stop-color="#ff0055" stop-opacity="0.4" />
      <stop offset="100%" stop-color="#ff0055" stop-opacity="0" />
    </linearGradient>
    <linearGradient id="qrFloorGrid" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="#a855f7" stop-opacity="0" />
      <stop offset="50%" stop-color="#a855f7" stop-opacity="0.3" />
      <stop offset="100%" stop-color="#00f0ff" stop-opacity="0.6" />
    </linearGradient>
  </defs>
  <rect width="1920" height="1080" fill="url(#qrArenaGlow)" />
  <polygon points="100,0 550,1080 250,1080" fill="url(#qrLaserBlue)" />
  <polygon points="1820,0 1370,1080 1670,1080" fill="url(#qrLaserRed)" />
  <path d="M0 720 H1920 M0 780 H1920 M0 850 H1920 M0 930 H1920 M0 1020 H1920" stroke="url(#qrFloorGrid)" stroke-width="2" />
  <line x1="960" y1="650" x2="0" y2="1080" stroke="url(#qrFloorGrid)" stroke-width="3" />
  <line x1="960" y1="650" x2="480" y2="1080" stroke="url(#qrFloorGrid)" stroke-width="2" />
  <line x1="960" y1="650" x2="1440" y2="1080" stroke="url(#qrFloorGrid)" stroke-width="2" />
  <line x1="960" y1="650" x2="1920" y2="1080" stroke="url(#qrFloorGrid)" stroke-width="3" />
  <ellipse cx="960" cy="850" rx="380" ry="120" fill="#130424" stroke="#ffea00" stroke-width="4" opacity="0.8" />
  <ellipse cx="960" cy="850" rx="260" ry="80" fill="none" stroke="#00f0ff" stroke-width="2" opacity="0.6" />
</svg>`);

/**
 * Logotipo Vectorial por Defecto para Reacción Rápida.
 * @constant {string}
 */
export const DEFAULT_LOGO_SVG = encodeSvgDataUri(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 200" width="500" height="200">
  <defs>
    <linearGradient id="logoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#ffea00" />
      <stop offset="50%" stop-color="#ff0055" />
      <stop offset="100%" stop-color="#00f0ff" />
    </linearGradient>
    <filter id="logoGlow" x="-20%" y="-20%" width="140%" height="140%">
      <feGaussianBlur stdDeviation="6" result="blur" />
      <feComposite in="SourceGraphic" in2="blur" operator="over" />
    </filter>
  </defs>
  <polygon points="250,15 480,50 430,165 250,190 70,165 20,50" fill="#0c0414" stroke="url(#logoGrad)" stroke-width="5" filter="url(#logoGlow)" />
  <polygon points="250,25 235,90 265,90 240,155 285,80 255,80" fill="#ffea00" filter="url(#logoGlow)" />
  <text x="250" y="180" font-family="Bangers, Impact, sans-serif" font-size="28" fill="#ffffff" text-anchor="middle" letter-spacing="4">DUELO 1 VS 1</text>
</svg>`);

/**
 * Tarjeta Roja / Alerta de Falso Inicio (Early Fault).
 * @constant {string}
 */
export const EARLY_FAULT_CARD_SVG = encodeSvgDataUri(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 280" width="200" height="280">
  <defs>
    <linearGradient id="cardGrad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#ef4444" />
      <stop offset="100%" stop-color="#991b1b" />
    </linearGradient>
    <filter id="cardGlow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="8" stdDeviation="10" flood-color="#ef4444" flood-opacity="0.8" />
    </filter>
  </defs>
  <rect x="15" y="15" width="170" height="250" rx="12" fill="url(#cardGrad)" stroke="#ffffff" stroke-width="4" filter="url(#cardGlow)" />
  <polygon points="100,50 160,155 40,155" fill="#ffffff" stroke="#991b1b" stroke-width="3" />
  <text x="100" y="145" font-family="Impact, sans-serif" font-size="42" fill="#991b1b" text-anchor="middle" font-weight="bold">!</text>
  <text x="100" y="200" font-family="Bangers, Impact, sans-serif" font-size="22" fill="#ffffff" text-anchor="middle" letter-spacing="2">FALSO</text>
  <text x="100" y="230" font-family="Bangers, Impact, sans-serif" font-size="22" fill="#ffffff" text-anchor="middle" letter-spacing="2">INICIO</text>
</svg>`);

/**
 * Obtiene la URL del Sprite adecuado para un duelista dado según su bando ('player1'/'player2') y fase/estado.
 * Aplica resolución de URLs del tema y fallback vectoriales inmutables para garantizar 0% errores visuales.
 * 
 * @function GetQuickReactionSprite
 * @param {Object|null} theme - Objeto de configuración del tema visual.
 * @param {string} playerSide - Identificador del lado ('player1' / 'player2' / 'J1' / 'J2').
 * @param {string|number} state - Fase o estado del sprite ('back' | 'front' | 'shoot' | 'victory' | 1..4).
 * @returns {string} URL absoluta o Data URI SVG del sprite correspondiente.
 */
export const GetQuickReactionSprite = (theme, playerSide, state) => {
    const isPlayer2 = String(playerSide || '').toLowerCase().includes('2') ||
                      String(playerSide || '').toLowerCase().includes('j2') ||
                      String(playerSide || '').toLowerCase().includes('right');

    const normState = String(state || '').toLowerCase();

    // Estado 1: Espaldas (random_wait / tensión)
    if (normState === 'back' || normState === '1' || normState === 'random_wait' || normState === 'waiting') {
        if (isPlayer2) {
            return ResolveQuickReactionMediaUrl(theme?.j2SpriteBackUrl || theme?.J2SpriteBackUrl, DEFAULT_J2_BACK_SVG);
        }
        return ResolveQuickReactionMediaUrl(theme?.j1SpriteBackUrl || theme?.J1SpriteBackUrl, DEFAULT_J1_BACK_SVG);
    }

    // Estado 3: Disparo (shoot / disparando)
    if (normState === 'shoot' || normState === '3' || normState === 'disparo' || normState === 'shooting') {
        if (isPlayer2) {
            return ResolveQuickReactionMediaUrl(theme?.j2SpriteShootUrl || theme?.J2SpriteShootUrl, DEFAULT_J2_SHOOT_SVG);
        }
        return ResolveQuickReactionMediaUrl(theme?.j1SpriteShootUrl || theme?.J1SpriteShootUrl, DEFAULT_J1_SHOOT_SVG);
    }

    // Estado 4: Victoria (victory / winner / celebrando)
    if (normState === 'victory' || normState === '4' || normState === 'winner' || normState === 'celebrating') {
        if (isPlayer2) {
            return ResolveQuickReactionMediaUrl(theme?.j2SpriteVictoryUrl || theme?.J2SpriteVictoryUrl, DEFAULT_J2_VICTORY_SVG);
        }
        return ResolveQuickReactionMediaUrl(theme?.j1SpriteVictoryUrl || theme?.J1SpriteVictoryUrl, DEFAULT_J1_VICTORY_SVG);
    }

    // Estado 2: Frente por defecto (game_ready, trigger_active, lobby, idle, countdown)
    if (isPlayer2) {
        return ResolveQuickReactionMediaUrl(theme?.j2SpriteFrontUrl || theme?.J2SpriteFrontUrl, DEFAULT_J2_FRONT_SVG);
    }
    return ResolveQuickReactionMediaUrl(theme?.j1SpriteFrontUrl || theme?.J1SpriteFrontUrl, DEFAULT_J1_FRONT_SVG);
};

export default {
    ResolveQuickReactionMediaUrl,
    PreloadQuickReactionThemeAssets,
    GetQuickReactionSprite,
    DEFAULT_J1_BACK_SVG,
    DEFAULT_J1_FRONT_SVG,
    DEFAULT_J1_SHOOT_SVG,
    DEFAULT_J1_VICTORY_SVG,
    DEFAULT_J2_BACK_SVG,
    DEFAULT_J2_FRONT_SVG,
    DEFAULT_J2_SHOOT_SVG,
    DEFAULT_J2_VICTORY_SVG,
    DEFAULT_SCREEN_BG_SVG,
    DEFAULT_LOGO_SVG,
    EARLY_FAULT_CARD_SVG
};
